import { doc, getDoc } from 'firebase/firestore';
import { PLATFORM_COLLECTIONS, PLATFORM_ROLES } from './platformSchema';
import { loadCollectionRecords, loadRecordsByEquality } from './firestoreQueryHelpers';

function normalizeOrganizationRecord(record = {}, fallbackId = '') {
  const id = record.id || record.organizationId || fallbackId || '';

  return {
    id,
    organizationId: id,
    name: record.name || record.displayName || record.title || id,
    // Pass 88.1 — EXPOSE the canonical human display label distinctly (previously it was folded into `name`
    // only as a fallback and then dropped, so `organizations/{id}.displayName` = "Voletus Pilot" never
    // reached the UI and a stale membership label "Motus Pilot 2" showed instead). Human-facing surfaces use
    // `displayName || name`; the organizationId stays the stable identifier underneath (unchanged).
    displayName: record.displayName || '',
    slug: record.slug || '',
    status: record.status || 'active',
    industry: record.industry || '',
    disciplines: Array.isArray(record.disciplines) ? record.disciplines.filter(Boolean) : [],
    branding: record.branding || {},
    labels: record.labels || {},
    subscriptionPlan: record.subscriptionPlan || '',
    // Pass 82C — organization runtime profile (tenant-neutral generic context). Pass-through so the selected-org
    // record carries them to generic runtime (timezone/season/category-label resolution).
    timezone: record.timezone || record.timeZone || '',
    currentSeasonId: record.currentSeasonId || record.seasonId || '',
    categoryLabels: record.categoryLabels || {},
    isPilot: record.isPilot === true,
  };
}

// Pass 87.2 — exported so the ONE operations data session (useOperationsData) can normalise the
// realtime organizationMembers snapshot to the SAME shape the CRM one-shot loaders produce, letting
// Overview/Team/Clients and the schedule share a single member source without shape drift.
export function normalizeMembershipRecord(record = {}, organizationsById = {}) {
  const organizationId = record.organizationId || record.orgId || '';
  if (!organizationId) return null;

  const organization = organizationsById[organizationId];

  return {
    id: record.id || `${record.userId || 'unknown'}::${organizationId}`,
    userId: record.userId || '',
    organizationId,
    // Pass 88.1 — when the membership carries no label, prefer the canonical org displayName → name → id.
    organizationName: record.organizationName || organization?.displayName || organization?.name || organizationId,
    role: record.role || record.orgRole || PLATFORM_ROLES.INSTRUCTOR,
    status: record.status || 'active',
    labels: record.labels || {},
    // Instructor pool: 'internal' | 'collective'. Pass 46E: do NOT default a missing
    // pool to 'internal' — that silently mislabels unknown instructors as SAS School.
    // Category is resolved canonically at read time (see utils/instructorCategory.js).
    instructorPool: record.instructorPool || '',
    // Pass 43: instructor capability + SAS category (Sam workbook — items 6/7)
    instructorCategory: record.instructorCategory || '',
    // Pass 46E: additive per-season affiliation history (source-truthed; may differ per season)
    seasonMemberships: record.seasonMemberships || {},
    skiLevel: record.skiLevel != null ? record.skiLevel : null,
    snowboardLevel: record.snowboardLevel != null ? record.snowboardLevel : null,
    primaryDiscipline: record.primaryDiscipline || '',
    // Display name passthrough for CRM views
    displayName: record.displayName || record.name || '',
    name: record.name || record.displayName || '',
    // Pass 48 §3/§7/§25: preserve the fields downstream (Team, roster, capability,
    // Auth-linkage, analytics) rely on — normalisation must not silently drop them.
    ...(record.currentTeam !== undefined ? { currentTeam: record.currentTeam } : {}),
    ...(record.historicalOnly !== undefined ? { historicalOnly: record.historicalOnly } : {}),
    ...(record.isTeachingInstructor !== undefined ? { isTeachingInstructor: record.isTeachingInstructor } : {}),
    ...(record.jobFunction ? { jobFunction: record.jobFunction } : {}),
    // Pass 83B §10: preserve the per-member schedule visibility override so the instructor's live
    // My Schedule can compute its EFFECTIVE publishing policy (School horizon vs Collective self-managed)
    // from userData.organizationMemberships without a second organizationMembers read. Dropping it silently
    // forced the org default and made the "Published through…" copy inaccurate.
    ...(record.scheduleVisibilityOverride ? { scheduleVisibilityOverride: record.scheduleVisibilityOverride } : {}),
    ...(record.authUserId ? { authUserId: record.authUserId } : {}),
    ...(record.testAccount !== undefined ? { testAccount: record.testAccount } : {}),
    ...(record.excludeFromAnalytics !== undefined ? { excludeFromAnalytics: record.excludeFromAnalytics } : {}),
    ...(record.excludeFromPayroll !== undefined ? { excludeFromPayroll: record.excludeFromPayroll } : {}),
    ...(record.rosterSource ? { rosterSource: record.rosterSource } : {}),
  };
}

export function getLegacyOrganizationMemberships(userData = {}) {
  if (!userData?.orgId) return [];

  return [
    {
      id: `legacy::${userData.orgId}`,
      userId: userData.uid || '',
      organizationId: userData.orgId,
      organizationName: userData.orgName || userData.orgId,
      role:
        userData.orgRole ||
        (userData.role === 'admin'
          ? PLATFORM_ROLES.ORGANIZATION_ADMIN
          : userData.role === 'organization'
            ? PLATFORM_ROLES.ORGANIZATION_OWNER
            : userData.role === 'instructor'
              ? PLATFORM_ROLES.INSTRUCTOR
              : PLATFORM_ROLES.STUDENT),
      status: 'active',
      labels: userData.orgLabels || {},
      source: 'legacy_user_fields',
    },
  ];
}

// Pass 88.1 — the ONE human-facing organisation label precedence: the CANONICAL org metadata WINS over a
// stale denormalised membership label. displayName → name → cached membership name → organizationId (honest
// final fallback, never fabricated). Used wherever an org is shown to a person (switcher, Operations header,
// Student/QA preview). The organizationId is NEVER a display label except as the last-resort fallback.
export function resolveOrgDisplayLabel(orgRecord = {}, membership = {}) {
  return (orgRecord && orgRecord.displayName)
    || (orgRecord && orgRecord.name)
    || (membership && membership.organizationName)
    || (membership && membership.organizationId)
    || (orgRecord && orgRecord.organizationId)
    || '';
}

export function buildOrganizationsById(organizations = []) {
  return (organizations || []).reduce((acc, organization) => {
    const normalized = normalizeOrganizationRecord(organization, organization?.id);
    if (normalized.organizationId) {
      acc[normalized.organizationId] = normalized;
    }
    return acc;
  }, {});
}

export function resolveOrganizationMemberships(
  userData = {},
  organizationMembers = [],
  organizationsById = {}
) {
  const normalizedMembers = (organizationMembers || [])
    .map((member) => normalizeMembershipRecord(member, organizationsById))
    .filter((member) => member?.organizationId)
    .filter((member) => member.status !== 'revoked');

  if (normalizedMembers.length > 0) {
    return normalizedMembers;
  }

  if (Array.isArray(userData?.organizationMemberships) && userData.organizationMemberships.length > 0) {
    return userData.organizationMemberships
      .filter((membership) => membership?.organizationId)
      .map((membership) =>
        normalizeMembershipRecord(
          {
            ...membership,
            userId: membership.userId || userData.uid || '',
            // Pass 88.1 — canonical org metadata (displayName → name) WINS over a stale denormalised
            // membership label; falls back to the membership name, then the id (honest final fallback).
            organizationName: resolveOrgDisplayLabel(organizationsById[membership.organizationId], membership),
          },
          organizationsById
        )
      )
      .filter(Boolean);
  }

  return getLegacyOrganizationMemberships(userData);
}

export async function loadOrganizationsById(db) {
  try {
    return buildOrganizationsById(
      await loadCollectionRecords(db, PLATFORM_COLLECTIONS.ORGANIZATIONS)
    );
  } catch (error) {
    console.warn('Falling back from organizations collection:', error);
    return {};
  }
}

export async function loadOrganizationsByIds(db, organizationIds = []) {
  const uniqueIds = Array.from(new Set((organizationIds || []).filter(Boolean)));
  if (!uniqueIds.length) {
    return {};
  }

  try {
    const organizationRecords = await Promise.all(
      uniqueIds.map(async (organizationId) => {
        const snapshot = await getDoc(doc(db, PLATFORM_COLLECTIONS.ORGANIZATIONS, organizationId));
        return snapshot.exists()
          ? { id: snapshot.id, ...snapshot.data() }
          : null;
      })
    );

    return buildOrganizationsById(organizationRecords.filter(Boolean));
  } catch (error) {
    console.warn('Falling back from organization doc fetches:', error);
    return {};
  }
}

// Pass 58B — resolve a signed-in user's memberships by BOTH identity fields. The canonical linked-member
// model stores `member.userId` = permanent business identity (e.g. `seed_instructor_dom`) and
// `member.authUserId` = the Firebase Auth UID. A login therefore matches via `authUserId == uid` (the
// canonical case) OR the legacy `userId == uid` case (older members whose userId happened to be the UID).
// Query both and DEDUPE by the canonical member doc id so a dual-match returns exactly once. The business
// `userId` is never migrated to the Auth UID.
export async function loadOrganizationMembershipsForUser(db, currentUser) {
  if (!currentUser?.uid) {
    return [];
  }
  const uid = currentUser.uid;
  const load = async (field) => {
    try {
      return await loadRecordsByEquality(db, {
        collectionName: PLATFORM_COLLECTIONS.ORGANIZATION_MEMBERS,
        field,
        value: uid,
      });
    } catch (error) {
      console.warn(`Falling back from organizationMembers ${field} query:`, error);
      return [];
    }
  };
  const [byAuth, byLegacy] = await Promise.all([load('authUserId'), load('userId')]);
  const byId = new Map();
  for (const rec of [...byAuth, ...byLegacy]) {
    const key = rec.id || (rec.userId && rec.organizationId ? `${rec.userId}::${rec.organizationId}` : `${rec.userId || ''}|${rec.organizationId || ''}`);
    if (!byId.has(key)) byId.set(key, rec);
  }
  return Array.from(byId.values());
}

export async function loadOrganizationMembers(db, organizationId = '') {
  if (!organizationId) {
    return [];
  }

  try {
    const records = await loadRecordsByEquality(db, {
      collectionName: PLATFORM_COLLECTIONS.ORGANIZATION_MEMBERS,
      field: 'organizationId',
      value: organizationId,
    });
    return records
      .map((membership) => normalizeMembershipRecord(membership));
  } catch (error) {
    console.warn('Falling back from organization member list:', error);
    return [];
  }
}

export async function loadUserOrganizationData(
  db,
  { currentUser = null, userData = {} } = {}
) {
  const rawMemberships = await loadOrganizationMembershipsForUser(db, currentUser);
  const candidateOrganizationIds = Array.from(
    new Set(
      [
        ...rawMemberships.map((membership) => membership.organizationId || membership.orgId || ''),
        userData?.orgId || '',
      ].filter(Boolean)
    )
  );
  const organizationsById = candidateOrganizationIds.length
    ? await loadOrganizationsByIds(db, candidateOrganizationIds)
    : await loadOrganizationsById(db);

  const memberships = resolveOrganizationMemberships(
    { ...(userData || {}), uid: userData?.uid || currentUser?.uid || '' },
    rawMemberships,
    organizationsById
  );

  return {
    organizationsById,
    memberships,
    source: rawMemberships.length > 0 ? 'organization_members' : 'legacy_user_fields',
  };
}
